import {initReactI18next} from 'react-i18next'

import i18n from 'i18next'

import fallbackEn from '@progyny/locales/en.json'
import {REACT_APP_BACKEND_URL, isDevelopment} from '@progyny/utils/env'

// Cache to avoid redundant API calls
const loadedLanguages = new Set<string>()

// Function to fetch translations dynamically
const fetchTranslations = async (lng: string): Promise<Record<string, any>> => {
  const previewToken = new URLSearchParams(window.location.search)
  const token = previewToken.get('preview')
  try {
    if (loadedLanguages.has(lng)) return {} // Prevent duplicate calls
    const BACKEND_URL =
      document.location.hostname === 'member.apryl.co'
        ? 'https://backend.apryl.co/employees'
        : `${REACT_APP_BACKEND_URL}`
    const url = token
      ? `${BACKEND_URL}/translations/${lng}/?preview=${token}`
      : `${BACKEND_URL}/translations/${lng}/`
    const response = await fetch(url, {credentials: 'include'})
    const translations = await response.json()

    loadedLanguages.add(lng) // Mark language as loaded
    return translations
  } catch (error) {
    console.error(`Error fetching translations for ${lng}:`, error)
    return {}
  }
}

// Initialize i18n with the bundled en.json as an immediate fallback so the app can
// paint right away. Backend translations are fetched asynchronously and then
// override these values via addResourceBundle.
i18n.use(initReactI18next).init({
  fallbackLng: 'en',
  debug: isDevelopment ? true : false,
  interpolation: {escapeValue: false},
  resources: {en: {translation: fallbackEn as Record<string, string>}},
  nsSeparator: '|:',
})

// Loads translations for the given language and notifies consumers to re-render.
// When skipChangeLanguage is false the UI language is switched on success; when true
// (called from the languageChanged listener below) the language is already set and
// we only need to fetch + emit so react-i18next consumers re-render with fresh data.
const updateTranslations = async (
  lng: string,
  skipChangeLanguage = false
): Promise<boolean> => {
  if (!loadedLanguages.has(lng)) {
    const newTranslations = await fetchTranslations(lng)

    if (Object.keys(newTranslations).length === 0) {
      // Backend returned nothing; for English we proceed with the bundled fallback.
      if (lng === 'en') {
        if (!skipChangeLanguage) await i18n.changeLanguage('en')
        return true
      }
      return false
    }

    // Backend is the source of truth: overwrite any existing keys (including the
    // bundled en.json fallback) with the freshly fetched values.
    i18n.addResourceBundle(lng, 'translation', newTranslations, true, true)

    if (!skipChangeLanguage) {
      // changeLanguage emits 'languageChanged', which triggers a re-render of all
      // react-i18next consumers with the freshly loaded bundle.
      await i18n.changeLanguage(lng)
      return true
    }

    // Language is already set; emit so consumers re-render with the new bundle.
    i18n.emit('languageChanged', lng)
    return true
  }

  if (!skipChangeLanguage) {
    await i18n.changeLanguage(lng)
  }

  return true
}

// When the language changes (from anywhere), make sure its translations are loaded.
// Pass skipChangeLanguage=true to avoid re-entering changeLanguage (would recurse).
if (process.env.NODE_ENV !== 'test') {
  i18n.on('languageChanged', async (lng) => {
    await updateTranslations(lng, true)
  })
}

// Kick off the initial fetch in the background. The app renders immediately with
// the bundled en.json fallback; when this resolves the consumers re-render via the
// languageChanged emit inside updateTranslations.
const initialLanguage =
  localStorage.getItem('ui_language') || i18n.language || 'en'
if (process.env.NODE_ENV !== 'test') {
  void updateTranslations(initialLanguage, false)
}

export {updateTranslations}
export default i18n
